'use client';

import { useState } from 'react';
import styles from './AlexPlanScore.module.scss';
import { MedicareApiPlan } from 'types/api';
import { useAppSelector } from 'lib/hooks';
import Tooltip from 'components/Tooltip';
import CaretIcon from 'components/icons/CaretIcon';
import UIButton from 'components/buttons/UIButton';
import { selectHasRecommendation } from 'slices/selectors';
import type { PlanCardContext } from 'components/plan/planCardTypes';

type AlexPlanScoreProps = {
  plan: MedicareApiPlan | undefined;
  context?: PlanCardContext;
};

const PlanScoreGeneric = ({ type }: { type: string }) => {
  return (
    <>
      <p>
        <strong>This plan&rsquo;s greatest strengths are:</strong>
      </p>
      <ul>
        {type !== 'pdp' && (
          <>
            <li>Keeping your costs low compared to the other plans</li>
            <li>
              Protecting you for expected health care needs (based on your
              answers)
            </li>
            <li>Covering your preferred providers</li>
          </>
        )}
        {type === 'pdp' && <li>Keeping your prescription costs low</li>}
        <li>Earning a high Medicare Star Rating compared to the other plans</li>
      </ul>
    </>
  );
};

const PlanScoreMedSupp = () => {
  return (
    <>
      <p>
        <strong>
          This plan&rsquo;s strengths compared to other Medicare Supplement
          plans are:
        </strong>
      </p>
      <ul>
        <li>Avoiding high costs for visits and procedures</li>
        <li>
          Protecting you for expected health care needs (based on your answers)
        </li>
      </ul>
    </>
  );
};

const PlanScorePDP = () => {
  return (
    <>
      <p>
        <strong>
          This plan&rsquo;s strengths compared to other Part D prescription drug
          plans are:
        </strong>
      </p>
      <ul>
        <li>Keeping your prescription costs low</li>
        <li>Earning a high Medicare Star Rating compared to the other plans</li>
      </ul>
    </>
  );
};

const PlanScoreDynamic = ({ type }: { type: string }) => {
  if (type === 'gap') {
    return <PlanScoreMedSupp />;
  }
  if (type === 'pdp') {
    return <PlanScorePDP />;
  }

  return <PlanScoreGeneric type={type} />;
};

const AlexPlanScore = ({ plan, context }: AlexPlanScoreProps) => {
  const alexScore = Math.round(plan?.picwell_score || 0);
  const planType = plan?.type;
  const isCartContext = context === 'cart';
  const hasRecommendation = useAppSelector((state) =>
    selectHasRecommendation(state)
  );
  const [isOpen, setIsOpen] = useState(false);
  const planScoreInfoId = `alex-plan-score-info-${plan?.id}`;

  return (
    <div className={styles.planAlexScore}>
      <div className={styles.accordion}>
        {/* Header */}
        <div className={styles.alexScoreHeader}>
          <h3>ALEX Plan Score</h3>
          <Tooltip horizontalAlign="left" subject="ALEX Plan Score">
            Any plan with a score in the 90s indicates it&rsquo;s a good fit for
            your needs, compared to other available plans.
          </Tooltip>
          <div className={styles.planScoreActions}>
            <div className={styles.planScoreBadge}>
              {hasRecommendation ? alexScore : '--'}
            </div>
            <button
              type="button"
              className={`${styles.accordionToggle} ${
                isCartContext ? styles.cartContext : ''
              }`}
              aria-label={
                isOpen ? 'Hide plan score details' : 'Show plan score details'
              }
              aria-expanded={isOpen}
              aria-controls={planScoreInfoId}
              onClick={() => setIsOpen((prev) => !prev)}
            >
              <CaretIcon
                color="purple"
                direction={isOpen ? 'up' : 'down'}
                size="large"
              />
            </button>
          </div>
        </div>

        {/* Meter */}
        <div className={styles.planScoreProgress} aria-hidden="true">
          <div className={styles.planScoreProgressBg}></div>
          <div
            className={styles.planScoreProgressFill}
            style={{ width: `${hasRecommendation ? alexScore : 100}%` }}
          ></div>
        </div>
      </div>

      {/* Text  */}
      <div
        id={planScoreInfoId}
        className={`${styles.planScoreInfo} ${isOpen ? styles.isOpen : ''} ${
          isCartContext ? styles.cartContext : ''
        }`}
      >
        {hasRecommendation && (
          <div className={styles.scoreExplanation}>
            {alexScore >= 90 && <PlanScoreDynamic type={planType as string} />}
            {alexScore < 90 && (
              <p>
                Plans with a recommendation score of at least 90 may better fit
                your needs.
              </p>
            )}
          </div>
        )}

        {!hasRecommendation && (
          <div>
            <p>
              Let ALEX make you a recommendation to see how well this plan fits
              your needs and preferences.
            </p>
            <UIButton
              buttonType="tertiary"
              label="Get Started"
              icon={<CaretIcon direction="right" size="large" />}
              href="/check-audio"
              className={styles.getStartedBtn}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AlexPlanScore;
